import './Notifications.scss'

import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Row, Col, Container, Form, ListGroup, Spinner } from 'react-bootstrap'

import AccountMenu from 'components/common/navigation/AccountMenu'
import {
  requestNotificationAuthorization,
  stopScheduledNotifications
} from 'components/common/helpers/Mobile'
import {
  getUserPreferenceOptions,
  getUserPreferences,
  saveUserPreferences
} from 'components/common/api/Users'
import { navigateTo } from 'components/common/navigation'
import {
  ROUTE_NOTIFICATIONS,
  ROUTE_TODAY
} from 'components/common/helpers/Paths'
import {
  EVENT_ACTION_PAGEVIEW,
  EVENT_CATEGORY_REMINDERS,
  saveUserEvent
} from 'components/common/api/UserEvents'

/**
 * Notification component. Used to manage user notification preferences
 */
export default function Notifications({ setHeaderTitle, setReturnPath }) {
  const history = useHistory()

  // Define our days array
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ]

  // Utility function to get the day as a readable string,
  // depending on the day number and how many days are selected
  const _12HourTime = time => {
    const [hours, minutes] = time.split(':')
    const ampm = Number(hours) >= 12 ? 'PM' : 'AM'
    const hours12 = hours % 12 || 12
    return `${hours12}:${minutes} ${ampm}`
  }

  // Utility function to get the day as a readable string,
  // depending on the day number and how many days are selected
  const _readableDay = (day, index, selectedDays) => {
    // Sunday is the first day of the week and ios uses 1-7 for days
    const dayString = days[Number(day - 1)]
    if (selectedDays.length === 1) {
      return `${dayString}s`
    } else if (selectedDays.length === 7) {
      return index === 0 ? 'Everyday' : ''
    } else if (selectedDays.length === 2) {
      if (index === 0) {
        return `${dayString}s and `
      } else {
        return `${dayString}s`
      }
    } else if (selectedDays.length > 4) {
      return `${dayString.charAt(0)}${
        index === selectedDays.length - 1 ? '' : ', '
      }`
    } else if (selectedDays.length > 2) {
      return `${dayString.substring(0, 3)}${
        index === selectedDays.length - 1 ? '' : ', '
      }`
    }
  }

  // Get stored user notification preferences
  const [isLoaded, setIsLoaded] = useState(false)
  const [userPreferences, setUserPreferences] = useState()
  const [notificationsOptions, setNotificationsOptions] = useState()
  const [notificationsEnabled, setNotificationsEnabled] = useState(false)

  /**
   * Handler function for when the user toggles their notification preference
   */
  const enableNotifications = enable => {
    // If the user is enabling notifications, we need to request permission
    if (enable) requestNotificationAuthorization()
    else stopScheduledNotifications() // Disable notifications

    const notifications = {
      email: true, // TODO: Handle email notifications
      push: enable
    }
    const updatedPreferences = { ...userPreferences, notifications }

    // Update the user's preferences
    saveUserPreferences(updatedPreferences)
    setNotificationsEnabled(enable)
    setUserPreferences(updatedPreferences)
  }

  /**
   * Handler function for when the user selects a specific practice to be notified about
   * @param {Object} option The list option the user selected, which contains the practice to be notified about
   */
  const handleOptionClick = option => {
    navigateTo(`${ROUTE_NOTIFICATIONS}/${option.key}`, history)
  }

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0)

    // Set the header title
    setHeaderTitle('Reminders')

    // Set the return path
    setReturnPath(ROUTE_TODAY)

    // Get the user's notification preferences
    async function getUserPreferencesAsync() {
      const [options, preferences] = await Promise.all([
        getUserPreferenceOptions(),
        getUserPreferences()
      ])

      // Get and set all options for the user's notification preferences
      setNotificationsOptions(options.notifications) // all options
      setUserPreferences(preferences)
      setNotificationsEnabled(preferences?.notifications?.push)
      setIsLoaded(true)

      // Log access to this page
      saveUserEvent(EVENT_CATEGORY_REMINDERS, EVENT_ACTION_PAGEVIEW)
    }
    getUserPreferencesAsync()
  }, [setHeaderTitle, setReturnPath])

  return (
    <Container className='profile-container'>
      <Row xs={1} md={2}>
        {/* Sidebar panel for larger screens */}
        <Col md={4} className='mobile-hidden account-sidebar'>
          <AccountMenu />
        </Col>
        <Col md={8}>
          <h1 className='title'>Reminders</h1>
          <Form.Group
            controlId='formEnableNotifications'
            className='form-group green-group visible mb-3'
          >
            <h3>Switch on reminders</h3>
            <p>
              Receive daily reminders to take steps towards healther
              relationships and a better you
            </p>
            {/* Show spinner if we're waiting on loading */}
            {!isLoaded ? (
              <div className='text-center'>
                <Spinner
                  animation='border'
                  role='status'
                  className='inline-spinner'
                  variant='primary'
                  size='sm'
                >
                  <span className='visually-hidden'>Loading...</span>
                </Spinner>
                <small className='muted'> Checking your settings...</small>
              </div>
            ) : (
              <Form.Check
                id='enableNotifications'
                size='lg'
                label={
                  notificationsEnabled
                    ? 'Reminders are on'
                    : 'Turn On Reminders'
                }
                type='switch'
                onChange={e => enableNotifications(e.currentTarget.checked)}
                checked={notificationsEnabled}
                disabled={!isLoaded}
              />
            )}
          </Form.Group>
          <Form.Group
            controlId='formEnableNotifications'
            className={`form-group mb-3 p-4 ${
              notificationsEnabled ? 'visible' : ''
            }`}
          >
            <h4>
              <strong>Custom Reminders</strong>
            </h4>
            <p>
              Pick a custom day and time for each practice. You can change these
              at any time.
            </p>

            <ListGroup>
              {notificationsOptions?.map(option => (
                <ListGroup.Item
                  className='custom-notification-practice'
                  action
                  onClick={() => handleOptionClick(option)}
                  key={option.key}
                >
                  <span className='custom-notification-label'>
                    {option.title}
                    <br />
                    <small className='muted'>
                      {/* Split the days from the option string and show the first letter of each day */}
                      {option.key === 'affirmation' &&
                      userPreferences?.notifications?.['affirmation'] !== ''
                        ? 'Everyday'
                        : userPreferences?.notifications?.[
                            option.key
                          ]?.includes('|') &&
                          userPreferences?.notifications?.[option.key]
                            ?.split('|')[1]
                            .split(',')
                            .sort()
                            .map(_readableDay)}
                    </small>
                  </span>

                  {/* Do we have a custom notification scheduled defined for this practice? */}
                  {userPreferences?.notifications?.[option.key] && (
                    <span className='custom-notification-time'>
                      <i className='bi bi-check-circle green'></i>
                      <br />
                      {/* Split the time from the option string */}
                      <small className='muted'>
                        {(userPreferences?.notifications?.[
                          option.key
                        ]?.includes('|') ||
                          option.key === 'affirmation') &&
                          _12HourTime(
                            userPreferences?.notifications?.[option.key]?.split(
                              '|'
                            )[0]
                          )}
                      </small>
                    </span>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Form.Group>
        </Col>
      </Row>
    </Container>
  )
}
